/**
 * FalcaoPanel — pesquisa de jurisprudência no sistema Falcão (Justiça do Trabalho).
 * Ocupa a metade inferior do pane direito do RefinoSplit. Os resultados ficam
 * selecionáveis (checkbox); a seleção é compartilhada com o ChatRefinar via
 * window-event `falcao:selection` para que comandos do tipo
 *   "inclua as jurisprudências selecionadas entre 4 e 5"
 * possam anexar os itens ao payload de refinamento.
 *
 * Recursos:
 *   • Filtro por TRIBUNAL (TST + TRTs) — enviado ao backend (parâmetro `tribunais`).
 *   • Resultados ordenados por relevância (score) e com ementa preenchida pelo
 *     backend a partir do inteiro teor quando o acórdão não tem ementa formal.
 *   • "Ver inteiro teor" expande o texto do acórdão INLINE (o deep-link do SPA do
 *     Falcão exige idTribunal, que a API pública não fornece — então mostramos o
 *     conteúdo, que já vem na resposta da busca, em vez de um link quebrado).
 */
import { useEffect, useMemo, useState } from 'react';
import { apiBase } from '../api';

interface JurisprudenciaItem {
  id: string;
  tribunal: string;
  numero: string;
  ementa: string;
  inteiro_teor?: string;
  possui_ementa?: boolean;
  url?: string;
  data?: string;
  relator?: string;
  orgao_julgador?: string;   // turma / órgão julgador (compõe o cabeçalho da citação)
}

interface Props {
  idPrazo: number;
}

export function FalcaoPanel({ idPrazo }: Props) {
  const [q, setQ] = useState('');
  const [tribunal, setTribunal] = useState('');          // '' = todos
  const [tribunais, setTribunais] = useState<{ sigla: string; nome: string }[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [resultados, setResultados] = useState<JurisprudenciaItem[]>([]);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [expandido, setExpandido] = useState<Set<string>>(new Set());

  const selecionadas = useMemo(() => resultados.filter((r) => sel.has(r.id)), [resultados, sel]);

  // Carrega a lista de tribunais para o filtro (uma vez).
  useEffect(() => {
    fetch(`${apiBase()}/api/falcao/tribunais`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.tribunais) setTribunais(d.tribunais); })
      .catch(() => { /* filtro fica vazio; busca segue sem filtro */ });
  }, []);

  // Compartilha a seleção atual com o ChatRefinar via evento global.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('falcao:selection', { detail: { idPrazo, selecionadas } }));
  }, [idPrazo, selecionadas]);

  async function pesquisar(e?: React.FormEvent) {
    e?.preventDefault();
    if (!q.trim()) return;
    setCarregando(true);
    setErro(null);
    setExpandido(new Set());
    try {
      let url = `${apiBase()}/api/falcao/search?q=${encodeURIComponent(q.trim())}&limit=10`;
      if (tribunal) url += `&tribunais=${encodeURIComponent(tribunal)}`;
      const r = await fetch(url, { credentials: 'include' });
      if (!r.ok) {
        if (r.status === 503) throw new Error('Falcão temporariamente indisponível (bloqueio). Tente de novo em instantes.');
        throw new Error(`Falha na busca (HTTP ${r.status}).`);
      }
      const data = await r.json();
      const itens: JurisprudenciaItem[] = (data?.resultados ?? []).map((it: any, i: number) => ({
        id: String(it.id ?? it.id_documento ?? it.numero ?? `${it.tribunal ?? 'jt'}-${i}`),
        tribunal: String(it.tribunal ?? '—'),
        numero: String(it.numero ?? it.numero_processo ?? ''),
        ementa: String(it.ementa ?? it.snippet ?? ''),
        inteiro_teor: it.inteiro_teor ?? '',
        possui_ementa: !!it.possui_ementa,
        url: it.url || undefined,
        data: it.data ?? it.data_julgamento,
        relator: it.relator,
        orgao_julgador: it.orgao_julgador ?? it.turma ?? it.orgaoJulgador ?? undefined,
      }));
      setResultados(itens);
    } catch (e: any) {
      setErro(e?.message ?? 'erro');
      setResultados([]);
    } finally {
      setCarregando(false);
    }
  }

  function toggle(id: string) {
    setSel((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleTeor(id: string) {
    setExpandido((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-950/40 border-t border-slate-200 dark:border-slate-800">
      <form onSubmit={pesquisar} className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/60">
        <span className="text-xs font-display font-semibold text-blue-700 dark:text-amber-300">⚖️ Falcão</span>
        <select
          value={tribunal}
          onChange={(e) => setTribunal(e.target.value)}
          title="Filtrar por tribunal"
          className="text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-1.5 py-1 outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="">Todos tribunais</option>
          {tribunais.map((t) => (
            <option key={t.sigla} value={t.sigla}>{t.sigla}</option>
          ))}
        </select>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder='ex.: "adicional de risco portuário"'
          className="flex-1 min-w-[140px] text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-blue-500"
        />
        <button
          type="submit"
          disabled={carregando || !q.trim()}
          className="text-xs px-2.5 py-1 rounded bg-blue-600 text-white font-semibold disabled:opacity-40"
        >
          {carregando ? '…' : 'Pesquisar'}
        </button>
        <span className="text-[10px] font-mono text-slate-400" title="selecionadas">
          {selecionadas.length}/{resultados.length}
        </span>
      </form>

      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
        {erro && (
          <div className="text-xs text-red-600 dark:text-red-400 bg-red-500/10 border border-red-500/30 rounded p-2">
            {erro}
          </div>
        )}
        {!erro && !carregando && resultados.length === 0 && (
          <div className="text-xs text-slate-400 italic text-center py-6">
            Pesquise jurisprudência no Falcão (pode filtrar por tribunal). Selecione itens com o
            checkbox e use no terminal: <code className="font-mono">inclua as jurisprudências selecionadas entre 4 e 5</code>.
          </div>
        )}
        {resultados.map((r) => {
          const marcada = sel.has(r.id);
          const aberto = expandido.has(r.id);
          return (
            <div
              key={r.id}
              className={`rounded border transition ${
                marcada
                  ? 'bg-amber-500/10 border-amber-500/40'
                  : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'
              }`}
            >
              <label className="flex items-start gap-2 p-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={marcada}
                  onChange={() => toggle(r.id)}
                  className="mt-0.5 accent-amber-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-[10px] font-mono text-slate-500 dark:text-slate-400">
                    <span className="font-semibold text-blue-700 dark:text-amber-300">{r.tribunal}</span>
                    <span className="truncate">{r.numero}</span>
                    {r.data && <span>· {r.data}</span>}
                    {!r.possui_ementa && <span className="text-slate-400 italic">· sem ementa formal</span>}
                  </div>
                  <div className={`text-xs text-slate-800 dark:text-slate-100 leading-snug mt-0.5 ${aberto ? '' : 'line-clamp-4'}`}>
                    {r.ementa || <span className="italic text-slate-400">(sem texto)</span>}
                  </div>
                </div>
              </label>
              {(r.inteiro_teor || r.url) && (
                <div className="flex items-center gap-3 px-2 pb-1.5 pl-8">
                  {r.inteiro_teor && (
                    <button
                      type="button"
                      onClick={() => toggleTeor(r.id)}
                      className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {aberto ? 'recolher ▲' : 'ver inteiro teor ▾'}
                    </button>
                  )}
                  {r.url && (
                    <a href={r.url} target="_blank" rel="noreferrer"
                       className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline">
                      abrir no Falcão ↗
                    </a>
                  )}
                </div>
              )}
              {aberto && r.inteiro_teor && (
                <div className="mx-2 mb-2 ml-8 max-h-72 overflow-y-auto rounded bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 p-2 text-[11px] leading-relaxed text-slate-700 dark:text-slate-200 whitespace-pre-wrap">
                  {r.inteiro_teor}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
